import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Grid, GridCell, GridRow } from '@mobi/component-library/Common/Grid'
import { post, queryKeys } from '@mobi/account/Utils'
import { Button, InfoBox, Input } from '@mobi/component-library/Common/V2'
import { Modal } from '@mobi/component-library/Overlays/Modal'
import { Spinner } from '@mobi/component-library/Common/Spinner'
import { BetLimitLegal, ModalFooterStyled, BetLimitFormStyled } from './BetLimit.styles'
import type { PrecommitmentResponse } from '@mobi/account/Areas/SignUp/Utils/SignUpApi'
import { precommitmentUrl } from '@mobi/account/Utils'
import { CustomerServiceTelephone } from '@mobi/component-library/Common/V2'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { toMoney } from '@mobi/utils/money'
import { dayjs, formatDate } from '@mobi/utils/date'
import { trackEditBetLimit, trackNeedAssistance, trackRemoveBetLimit } from './Utils/ga'

const DEFAULT_MAX_BET_LIMIT = 999999
const DEFAULT_MIN_BET_LIMIT = 1

export const BetLimitForm = ({
  precommitmentDetails,
  hasDataFetchFailed,
}: {
  precommitmentDetails: PrecommitmentResponse | undefined
  hasDataFetchFailed: boolean
}): JSX.Element => {
  const queryClient = useQueryClient()
  const [newBetLimit, setNewBetLimit] = React.useState(
    precommitmentDetails?.BetLimit?.toString() || ''
  )
  const [isRemovingBetLimit, setIsRemovingBetLimit] = React.useState(false)
  const [isCoolingOffConfirmationModalOpen, setIsCoolingOffConfirmationModalOpen] =
    React.useState(false)

  const maxBetLimit = precommitmentDetails?.BetLimitRange?.Max || DEFAULT_MAX_BET_LIMIT
  const minBetLimit = precommitmentDetails?.BetLimitRange?.Min || DEFAULT_MIN_BET_LIMIT
  const defaultCoolingOffPeriodDays = 7
  const coolingOffPeriodDays =
    precommitmentDetails?.CoolingOffPeriodDays || defaultCoolingOffPeriodDays

  const mutation = useMutation({
    mutationFn: () =>
      post<string, PrecommitmentResponse>(
        isRemovingBetLimit ? precommitmentUrl.optOutBetLimit : precommitmentUrl.updateBetLimit,
        isRemovingBetLimit ? undefined : newBetLimit
      ),

    onSuccess: () => {
      if (isRemovingBetLimit) {
        trackEditBetLimit(newBetLimit)
      } else {
        trackRemoveBetLimit()
      }
      addToast({
        id: 'bet-limit-success',
        isDismissable: true,
        title: 'Your bet limit has been set.',
        icon: 'success',
      })
      queryClient.invalidateQueries({ queryKey: [queryKeys.precommitmentDetails] })
    },
  })

  const isBetLimitPendingRemoval =
    precommitmentDetails?.ProposedBetLimit?.ActiveDate &&
    !precommitmentDetails?.ProposedBetLimit?.Amount
  const canRemoveBetLimit = precommitmentDetails?.BetLimit && !isBetLimitPendingRemoval

  const editNewBetLimit = React.useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      // Remove all non-number characters.
      const attemptedNewValue = parseFloat(evt.target.value.replace(/[^0-9.-]/g, ''))
      if (isNaN(attemptedNewValue) || attemptedNewValue < 1) {
        setNewBetLimit('')
        return
      }

      if (attemptedNewValue < minBetLimit) {
        setNewBetLimit(minBetLimit.toString())
        return
      }

      if (attemptedNewValue > maxBetLimit) {
        setNewBetLimit(maxBetLimit.toString())
        return
      }

      setNewBetLimit(Math.trunc(attemptedNewValue).toString())
    },
    [maxBetLimit, minBetLimit]
  )

  if (hasDataFetchFailed) {
    return (
      <InfoBox color='orange' size='sm'>
        Unable to display your current bet limit. Please try again later.
      </InfoBox>
    )
  }

  const PendingBetLimitInfoBox = () => {
    if (!precommitmentDetails?.ProposedBetLimit?.ActiveDate) return null

    const isOptingOut = !precommitmentDetails?.ProposedBetLimit?.Amount

    const pendingLimitMessage = isOptingOut
      ? 'Your bet limit will be removed on the '
      : `Your bet limit of ${toMoney(precommitmentDetails?.ProposedBetLimit?.Amount ?? 0, { decimalPlaces: 0 })} will be effective on the `

    return (
      <InfoBox color='orange' size='md'>
        <div>
          {pendingLimitMessage}
          <strong>
            {formatDate(
              dayjs(precommitmentDetails?.ProposedBetLimit?.ActiveDate, 'D/M/YYYY').toISOString(),
              'DD MMM YYYY'
            )}
          </strong>
          , after the mandatory {coolingOffPeriodDays} day cooling off period.
        </div>
      </InfoBox>
    )
  }

  const isSaveButtonDisabled = () => {
    const hasLimitChanged =
      newBetLimit !==
      (precommitmentDetails?.ProposedBetLimit?.Amount?.toString() ??
        precommitmentDetails?.BetLimit?.toString())

    return !newBetLimit || !hasLimitChanged
  }

  return (
    <BetLimitFormStyled>
      {mutation.isPending && <Spinner />}
      {mutation.isError && <ErrorInfoBox />}

      <CoolingOffConfirmationModal
        coolingOffPeriodDays={coolingOffPeriodDays}
        isOpen={isCoolingOffConfirmationModalOpen}
        onConfirm={() => {
          mutation.mutate()
          setIsCoolingOffConfirmationModalOpen(false)
        }}
        onCancel={() => setIsCoolingOffConfirmationModalOpen(false)}
      />

      <PendingBetLimitInfoBox />

      <Input
        id='new-bet-limit'
        name='new-bet-limit'
        label='Single Bet Limit'
        value={newBetLimit}
        onChange={editNewBetLimit}
        min={DEFAULT_MIN_BET_LIMIT}
        max={DEFAULT_MAX_BET_LIMIT}
        maxLength={DEFAULT_MAX_BET_LIMIT.toFixed(0).length}
        type='text'
        inputMode='numeric'
        prefix={newBetLimit.length > 0 && '$'}
        placeholder='Please enter an amount'
        helpText='Set the amount you wish to be the limit of a single bet'
      />
      <Grid margin='0.2rem 0 0 0'>
        <GridRow>
          {canRemoveBetLimit && (
            <GridCell padding='0 0.4em 0 0'>
              <Button
                id='bet-limit-optout'
                type='button'
                color='secondary_grey'
                onClick={() => {
                  setIsRemovingBetLimit(true)
                  setIsCoolingOffConfirmationModalOpen(true)
                }}
                isFullWidth
              >
                Remove Limit
              </Button>
            </GridCell>
          )}
          <GridCell padding={canRemoveBetLimit ? '0 0 0 0.4em' : '0'}>
            <Button
              id='bet-limit-edit'
              type='button'
              color='primary'
              onClick={() => {
                setIsRemovingBetLimit(false)
                setIsCoolingOffConfirmationModalOpen(true)
              }}
              disabled={isSaveButtonDisabled()}
              isFullWidth
            >
              Save
            </Button>
          </GridCell>
        </GridRow>
      </Grid>

      <BetLimitLegal>
        We want to help you bet within your means, so that your TABtouch experience is a positive
        one.
        <br />
        <br />
        You can set a single bet limit for each bet you place.
        <br />
        <br />
        This limit applies to both racing and sports betting and must be in whole dollars.
        Increasing and removing the bet limit from your account will be subject to a{' '}
        {coolingOffPeriodDays} day cooling off period.
        <br />
        <br />
        If you require any assistance, please call us on{' '}
        <CustomerServiceTelephone onClick={trackNeedAssistance} />.
      </BetLimitLegal>
    </BetLimitFormStyled>
  )
}

const ErrorInfoBox = () => {
  return (
    <InfoBox color='red' size='md'>
      <b>Bet limit error</b>
      <div>
        Sorry, a network issue has prevented us from updating your bet limit. Please try again, or
        contact us on <CustomerServiceTelephone />{' '}
      </div>
    </InfoBox>
  )
}

type CoolingOffConfirmationModalProps = {
  coolingOffPeriodDays: number
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
}

const CoolingOffConfirmationModal = ({
  coolingOffPeriodDays,
  isOpen,
  onConfirm,
  onCancel,
}: CoolingOffConfirmationModalProps) => (
  <Modal
    headerIcon='warning'
    headerIconAlignment='center'
    headerTitle='Confirm Bet Limit'
    headerSubtitle={`I understand that my bet limit is set, there will be a ${coolingOffPeriodDays} day cooling off period should I choose to increase the amount or frequency of the limit. Any limit decrease will be effective immediately.`}
    headerTextAlignment='center'
    isOpen={isOpen}
    dismissStatus='disallowed'
    footerComponent={
      <ModalFooterStyled>
        <Button isFullWidth color='primary' onClick={onConfirm}>
          Confirm
        </Button>

        <Button isFullWidth color='secondary_color' onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooterStyled>
    }
  />
)
